import React, { useMemo } from "react";
import { Control, Controller, FieldValues } from "react-hook-form";

import { Date, DateField } from "../../dates";

interface Props<TValues extends FieldValues> {
  id: string;
  name: string;
  control: Control<TValues>;
  label?: React.ReactNode;
  dataTestId?: string;
  disabled?: boolean;
  fontSize?: number;
  labelFontSize?: number;
}

export function FormDateField<TValues extends FieldValues>({
  id,
  label,
  name,
  control,
  dataTestId,
  disabled = false,
  fontSize,
  labelFontSize,
}: Props<TValues>) {
  const [day, month, year] = useMemo(
    () => [`${name}.day`, `${name}.month`, `${name}.year`],
    [name]
  );

  return (
    <Controller
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      name={name as any}
      control={control}
      render={({
        field: { ref, onBlur, value, ...rest },
        fieldState: { invalid, error, isTouched },
      }) => {
        return (
          <DateField
            {...rest}
            id={id}
            value={value as string}
            label={label}
            error={error?.message}
            isTouched={isTouched}
            dataTestId={dataTestId}
            fontSize={fontSize}
            labelFontSize={labelFontSize}>
            <Date.Month
              name={month}
              placeholder="MM"
              nextFocus={day}
              onBlur={onBlur}
              error={invalid}
              disabled={disabled}
              fontSize={fontSize}
              inputProps={{
                "data-testid": `${dataTestId}-month`,
              }}
            />
            <Date.Day
              name={day}
              placeholder="DD"
              nextFocus={year}
              previousFocus={month}
              onBlur={onBlur}
              error={invalid}
              disabled={disabled}
              fontSize={fontSize}
              inputProps={{
                "data-testid": `${dataTestId}-day`,
              }}
            />
            <Date.Year
              name={year}
              placeholder="YYYY"
              previousFocus={day}
              onBlur={onBlur}
              error={invalid}
              disabled={disabled}
              fontSize={fontSize}
              inputProps={{
                "data-testid": `${dataTestId}-year`,
              }}
            />
          </DateField>
        );
      }}
    />
  );
}

export default FormDateField;
